/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import FacebookLeadAdsForm, { FacebookLeadAdsFormAttrMap } from 'cio/models/facebook-lead-ads-form';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';
import { IdentifierKey } from 'cio/utils/identifiers';
import AuthService from 'cio/services/auth';

interface FormsFacebookLeadAdsFormSettingsMapFormFieldsArgs {
  form: FacebookLeadAdsForm;
  isDetailsPage?: boolean;
  isDisabled?: boolean;
}

const FacebookLeadgenFormEmailQuestionType = 'EMAIL';

function isAttrMapEmail(attrMap: FacebookLeadAdsFormAttrMap) {
  return attrMap.facebookQuestionType === FacebookLeadgenFormEmailQuestionType;
}

interface AttrMapForForm {
  facebookQuestionKey: string;
  facebookQuestionLabel: string;
  facebookQuestionType: string;
  cioAttr: string;
  checked: boolean;
  isEmail: boolean;
}

export default class FormsFacebookLeadAdsFormSettingsMapFormFields extends Component<FormsFacebookLeadAdsFormSettingsMapFormFieldsArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;

  @tracked attrMapsForForm!: AttrMapForForm[];

  @action
  setAttrMapsForForm() {
    let { attrMaps } = this.args.form;

    this.attrMapsForForm = (
      [
        attrMaps.find((m) => isAttrMapEmail(m)),
        ...attrMaps.filter((m) => !isAttrMapEmail(m)),
      ] as FacebookLeadAdsFormAttrMap[]
    ).map((fMap) => ({
      facebookQuestionKey: fMap.facebookQuestionKey,
      facebookQuestionLabel: fMap.facebookQuestionLabel,
      facebookQuestionType: fMap.facebookQuestionType,
      cioAttr: fMap.cioAttr,
      checked: Boolean(fMap.cioAttr),
      isEmail: isAttrMapEmail(fMap),
    }));
  }

  private updateAttrMapsForFormAtIndex(index: number, attrMapForForm: AttrMapForForm) {
    let attrMapsForForm = [...this.attrMapsForForm];
    attrMapsForForm[index] = attrMapForForm;
    this.attrMapsForForm = attrMapsForForm;
  }

  get selectableAttributes() {
    // don't allow mapping to identifier attributes: all Facebook Lead Ads forms are hard-coded to map to "email"
    let idAttributes: string[] = Object.values(IdentifierKey);

    return this.environment.sentAttributes.filter((k) => !idAttributes.includes(k));
  }

  get attrMapsForFormErrors() {
    return this.attrMapsForForm.map((m) => {
      // never show errors for:
      // - read-only "email" input
      // - disabled mappings (cioAttr is an empty string)
      if (m.isEmail || !m.cioAttr.trim()) {
        return [];
      }

      if (m.cioAttr.trim() === IdentifierKey.CioId) {
        return [`You cannot use ${IdentifierKey.CioId}`];
      }

      let attrMapsUsingThisCioAttr = this.args.form.attrMaps.filter((f) => f.cioAttr.trim() === m.cioAttr.trim());
      if (attrMapsUsingThisCioAttr.length > 1) {
        return ['Already mapped to another profile attribute.'];
      }

      return [];
    });
  }

  get canChangeFields() {
    if (this.args.form.get('isNew')) {
      return this.auth.hasPermission('journeys/forms/create');
    }

    return this.auth.hasPermission('journeys/forms/edit');
  }

  // Un-checking a form field keeps its current value in the form, clears it on the underlying model:
  @action updateChecked(index: number, checked: boolean) {
    let attrMapForForm = this.attrMapsForForm[index];
    if (attrMapForForm == null) {
      return;
    }
    attrMapForForm = {
      ...attrMapForForm,
      checked,
    };
    this.updateAttrMapsForFormAtIndex(index, attrMapForForm);

    let attrMap = this.args.form.attrMaps.find((am) => am.facebookQuestionKey === attrMapForForm.facebookQuestionKey);
    if (attrMap == null) {
      return;
    }
    let originalIndex = this.args.form.attrMaps.indexOf(attrMap);
    let clone = { ...attrMap, cioAttr: checked ? attrMapForForm.cioAttr : '' };
    this.args.form.updateAttrMapAtIndex(originalIndex, clone);
  }

  @action updateCioAttr(index: number, cioAttr: string) {
    cioAttr = cioAttr.trim();

    let attrMapForForm = this.attrMapsForForm[index];
    if (attrMapForForm == null) {
      return;
    }
    attrMapForForm = {
      ...attrMapForForm,
      cioAttr,

      // check if not checked, updated with valid cio_attr value:
      checked: cioAttr ? true : attrMapForForm.checked,
    };
    this.updateAttrMapsForFormAtIndex(index, attrMapForForm);

    let originalIndex = this.args.form.attrMaps.findIndex(
      (am) => am.facebookQuestionKey === attrMapForForm.facebookQuestionKey,
    );
    if (originalIndex === -1) {
      return;
    }
    let attrMap = this.args.form.attrMaps[originalIndex];
    let clone = { ...attrMap, cioAttr };
    this.args.form.updateAttrMapAtIndex(originalIndex, clone);
  }
}
